import React, { memo } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { themeGet } from '@styled-system/theme-get'
import { Box, Flex, Link, Hide } from 'pcln-design-system'
import { LanguageSelector, CurrencySelector } from '@pcln/selectors'
import { Phone as PhoneIcon } from 'pcln-icons'

import Account from '../Account'
import Navigation from '../Navigation'
import NavHeader from '../NavHeader'
import HeaderDialog from '../HeaderDialog'
import NavLogo from '../NavLogo'
import PromoHeader from '../PromoHeader'

import usePartner from '../../hooks/usePartner'
import useSSO from '../../hooks/useSSO'
import useHeaderPhoneNumber from '../../hooks/useHeaderPhoneNumber'
import translateContentfulPromoHeader from '../../utils/translateContentfulPromoHeader'
import { LARGE_HIDE_BREAKPOINTS, SMALL_HIDE_BREAKPOINTS } from '../../constants/headerHideBreakpoints'

const LargeHeaderContainer = styled(Flex)`
  -moz-osx-font-smoothing: initial;
  -webkit-font-smoothing: initial;
  align-items: center;
  background-color: ${(props) => props.backgroundColor};
  box-shadow: 0 1px 0 0 rgba(89, 106, 125, 0.5);
  font-size: 12.8px;
  height: ${(props) => props.height}px;
  justify-content: center;
  padding: 12px ${themeGet('space.4')};
  z-index: ${(props) => props.zIndex};
`

const SmallHeaderContainer = styled(Box)`
  z-index: ${(props) => props.zIndex};
`

const Content = styled(Flex)`
  width: 100%;
  height: 100%;
  max-width: 1440px;
`

const RightContent = styled(Flex)`
  width: 100%;
  height: 100%;
`

const PhoneNumberLink = styled(Link)`
  min-width: fit-content;
  white-space: nowrap;
  font-size: ${themeGet('fontSizes.1')}px;
`

function Header({
  className,
  contentfulPromoHeader,
  desktopZIndex,
  initialCurrency,
  isAuthenticated = true,
  mobileZIndex,
  onCurrencyChange,
  onLanguageChange,
}) {
  const [isOpen, setIsOpen] = React.useState(false)

  const headerConfig = usePartner('config.header')
  if (!headerConfig) {
    return null
  }

  const appName = usePartner('appName', '')

  const {
    height = 60,
    large: {
      backgroundColor = '#FEFEFE',
      color = '#001833',
      dropdownColor = 'primary',
      dropdownWidth = '220px',
      includeAccountInNav = false,
      linkAlignment = 'center',
      minBreakpoint = 'xl',
    } = {},
    currencySelector: currencyProps,
    languageSelector: languagesProps,
    linksOpenNewTab = true,
    logoAlignment,
    logoHref,
    logoPath = '',
    logoTitle = '',
    navigation = [],
    secondaryLogoHref,
    secondaryLogoPadding = 0,
    secondaryLogoPath,
    small,
  } = headerConfig

  // SSO sign out is part of the navigation
  const { isSignedIn } = useSSO()
  const hasAccountConfig = typeof headerConfig.large?.includeAccountInNav !== 'undefined'
  const hasNavigation = navigation.length > 0 || isSignedIn
  const linkTarget = linksOpenNewTab ? '_blank' : undefined
  const phoneNumber = useHeaderPhoneNumber()

  let promoHeaderProps
  let showPromoHeader = false

  if (contentfulPromoHeader) {
    promoHeaderProps = translateContentfulPromoHeader(contentfulPromoHeader)
    showPromoHeader = promoHeaderProps !== null
  }

  const common = {
    appName,
    align: linkAlignment,
    backgroundColor,
    color,
    hasNavigation,
    height,
    isAuthenticated,
    isOpen,
    linkAlignment,
    logoAlignment,
    logoHref,
    logoPath,
    navigation,
    phoneNumber,
    secondaryLogoHref,
    secondaryLogoPadding,
    secondaryLogoPath,
    target: linkTarget,
  }

  return (
    <Box id='global-header' className={className} width='100%'>
      {showPromoHeader && <PromoHeader {...promoHeaderProps} />}
      {/* Small header */}
      <Hide {...SMALL_HIDE_BREAKPOINTS[minBreakpoint]} width='100%'>
        <SmallHeaderContainer zIndex={mobileZIndex} width='100%'>
          <NavHeader {...common} {...small} zIndex={mobileZIndex} onClick={() => setIsOpen(true)} />
          <HeaderDialog
            {...common}
            {...small}
            currencies={currencyProps?.currencies}
            currencyAcceptedApps={currencyProps?.acceptedApps}
            currencyButtonProps={currencyProps?.buttonProps}
            initialCurrency={initialCurrency}
            onClose={() => setIsOpen(false)}
            onCurrencyChange={onCurrencyChange}
          />
        </SmallHeaderContainer>
      </Hide>
      {/* Large header */}
      <Hide {...LARGE_HIDE_BREAKPOINTS[minBreakpoint]} width='100%'>
        <LargeHeaderContainer height={height} backgroundColor={backgroundColor} zIndex={desktopZIndex}>
          <Content justifyContent='space-between' alignItems='center'>
            <NavLogo title={logoTitle} path={logoPath} href={logoHref} target={linkTarget} />
            <RightContent justifyContent='flex-end' alignItems='center'>
              <Navigation
                {...common}
                dropdownColor={dropdownColor}
                dropdownWidth={dropdownWidth}
                includeAccount={hasAccountConfig ? includeAccountInNav : false}
              />
              {phoneNumber && (
                <>
                  <PhoneIcon pr={1} color={color} />
                  <PhoneNumberLink href={`tel:${phoneNumber}`} color={color}>
                    {phoneNumber}
                  </PhoneNumberLink>
                </>
              )}
              {hasAccountConfig && !includeAccountInNav ? <Account /> : null}
              {languagesProps && (
                <LanguageSelector
                  {...languagesProps}
                  buttonProps={{ color }}
                  trapFocus={false}
                  onLanguageChange={onLanguageChange}
                />
              )}
              {currencyProps?.acceptedApps?.includes(appName) && (
                <CurrencySelector
                  {...currencyProps}
                  buttonProps={{ color }}
                  initialCurrency={initialCurrency}
                  trapFocus={false}
                  onChange={onCurrencyChange}
                />
              )}
              {secondaryLogoPath && (
                <NavLogo
                  color={color}
                  href={secondaryLogoHref}
                  leftPadding={secondaryLogoPadding}
                  path={secondaryLogoPath}
                  target={linkTarget}
                />
              )}
            </RightContent>
          </Content>
        </LargeHeaderContainer>
      </Hide>
    </Box>
  )
}

Header.displayName = 'Header'

Header.propTypes = {
  className: PropTypes.string,
  contentfulPromoHeader: PropTypes.object,
  desktopZIndex: PropTypes.number,
  initialCurrency: PropTypes.string,
  isAuthenticated: PropTypes.bool,
  mobileZIndex: PropTypes.number,
  onCurrencyChange: PropTypes.func,
  onLanguageChange: PropTypes.func,
}

Header.defaultProps = {
  mobileZIndex: 10,
  desktopZIndex: 1500,
}

export default memo(Header)
